// Prefetches menu data for QR dining; refs prevent duplicate calls. PinAuth handles PIN independently.

import { useCallback, useContext, useEffect, useRef } from "react";
import {
    QrCodeActions,
    ResponseContext,
    SwiggyContextHeader,
    usePlApiContextFetch,
} from "@swiggy-private/portal-dineout-context";
import { useRouter } from "next/compat/router";

import { fetchMenuData } from "../api";
import { STORAGE_KEYS, FAILURE_STATUS_CODE } from "../constants";
import { useCurtainRaiserPrefetch } from "./useCurtainRaiserPrefetch";
import { peekDrinkingAgeConsent } from "../utils/drinkingAgeConsent";
import { processMenuSuccessResponse, isMenuRoute } from "../utils/menu";
import { sendCustomNREvViaBlaze } from "../utils/newrelic";

interface IUseMenuDataPrefetchParams {
    restaurantId: string;
    tableId: string;
}

export const useMenuDataPrefetch = ({ restaurantId, tableId }: IUseMenuDataPrefetchParams) => {
    const router = useRouter();
    const { swiggyHeaders } = useContext(SwiggyContextHeader);
    const { dispatch, state } = useContext(ResponseContext);

    const makePlApiCall = usePlApiContextFetch();
    const lastRouteRef = useRef<string | null>(null);
    const hasFetchedRef = useRef({ menu: false });

    // Consent last fetched with — guards against duplicate calls on effect re-runs / dev double-invoke.
    const menuFetchedConsentRef = useRef<boolean | null>(null);
    // Holds the most recently scheduled prefetchMenu call. Each new call attaches itself after
    // this one, so calls run one at a time instead of overlapping. By the time a call gets its
    // turn, it checks menuFetchedConsentRef — if an earlier call already fetched this consent,
    // it's a no-op instead of a duplicate fetch.
    const menuFetchChainRef = useRef<Promise<boolean>>(Promise.resolve(true));

    const { prefetchCurtainRaiser, hasFetchedCurtainRef } = useCurtainRaiserPrefetch({
        restaurantId,
        tableId,
        dispatch,
    });

    const currentPathname = router?.pathname ?? "";
    const isOnMenuPage = isMenuRoute(currentPathname);

    // Callers go through prefetchMenu, which serializes concurrent calls for the latest consent.
    const runMenuFetch = useCallback(
        async (consent: boolean): Promise<boolean> => {
            if (!makePlApiCall) return false;

            dispatch({ type: QrCodeActions.MENU_SCREEN_LOADING, payload: {} });

            let localCart = {};
            try {
                localCart = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.LOCAL_CART) || "{}") || {};
                dispatch({ type: QrCodeActions.UPDATE_LOCAL_CART, payload: localCart });
            } catch {
                sendCustomNREvViaBlaze("session_storage_table_config_error", {}, swiggyHeaders);
            }

            try {
                const menuResponse = await fetchMenuData({ tableId, restaurantId, consentLDA: consent }, makePlApiCall);

                if (menuResponse?.statusCode === FAILURE_STATUS_CODE) {
                    dispatch({ type: QrCodeActions.MENU_SCREEN_ERROR, payload: {} });
                    return false;
                }

                // Landed successfully with this consent value — remember it so we don't refetch.
                menuFetchedConsentRef.current = consent;
                processMenuSuccessResponse({ menuResponse, localCart, dispatch, pinData: state.pin?.data });
                return true;
            } catch {
                dispatch({ type: QrCodeActions.MENU_SCREEN_ERROR, payload: {} });
                return false;
            }
        },
        [makePlApiCall, restaurantId, tableId, dispatch, swiggyHeaders, state.pin?.data],
    );

    const prefetchMenu = useCallback(
        (consent = true): Promise<boolean> => {
            const fetchIfNeeded = (): Promise<boolean> => {
                if (!makePlApiCall || !restaurantId || !tableId) return Promise.resolve(false);
                // An earlier call already fetched this consent by the time our turn came up.
                if (menuFetchedConsentRef.current === consent) return Promise.resolve(true);
                return runMenuFetch(consent);
            };

            // Wait for whatever call is currently running to finish — succeeded or failed,
            // we don't care which, we just don't want to overlap with it — then run this call.
            const waitForPriorCall = menuFetchChainRef.current.catch(() => undefined);
            const thisCall = waitForPriorCall.then(fetchIfNeeded);
            menuFetchChainRef.current = thisCall;
            return thisCall;
        },
        [makePlApiCall, restaurantId, tableId, runMenuFetch],
    );

    // Re-runs both fetches with an explicit consent (used by the age gate on decline).
    // Callers can `await` this to know when the fresh data has landed.
    const refetchWithConsent = useCallback(
        async (consent: boolean) => {
            // Doesn't reset hasFetchedRef/hasFetchedCurtainRef — that would re-fire the mount
            // effect's optimistic consent=true prefetch and clobber this value.
            const [menuOk, curtainOk] = await Promise.all([
                prefetchMenu(consent),
                prefetchCurtainRaiser(consent, true),
            ]);
            // Let the age gate keep its loader up and retry instead of revealing stale data.
            if (!menuOk || !curtainOk) {
                throw new Error("refetchWithConsent failed");
            }
        },
        [prefetchMenu, prefetchCurtainRaiser],
    );

    useEffect(() => {
        const lastRoute = lastRouteRef.current;
        lastRouteRef.current = currentPathname;

        if (!isOnMenuPage || !restaurantId || !tableId || !makePlApiCall || !router?.isReady) {
            return;
        }

        // null lastRoute means first visit; anything else non-menu means we're returning to it.
        const shouldRefetch = lastRoute !== null && !isMenuRoute(lastRoute);

        if (shouldRefetch) {
            // Returning from cart/orders — data may be stale (an order may have been placed).
            hasFetchedRef.current = { menu: false };
            menuFetchedConsentRef.current = null;
            dispatch({ type: QrCodeActions.MENU_SCREEN_RESET, payload: {} });
        }

        if (!hasFetchedRef.current.menu) {
            hasFetchedRef.current.menu = true;
            // Use the last known answer (if any) as the guess. Falls back to true (most users
            // agree) when storage has nothing (first-ever visit) or the LDA config isn't known
            // yet — the age gate's decline path corrects this via refetchWithConsent if wrong.
            peekDrinkingAgeConsent(tableId).then((consent) => prefetchMenu(consent ?? true));
        }

        if (!hasFetchedCurtainRef.current) {
            hasFetchedCurtainRef.current = true;
            peekDrinkingAgeConsent(tableId).then((consent) => prefetchCurtainRaiser(consent ?? true));
        }
    }, [
        isOnMenuPage,
        restaurantId,
        tableId,
        makePlApiCall,
        router?.isReady,
        currentPathname,
        prefetchMenu,
        prefetchCurtainRaiser,
        dispatch,
        hasFetchedCurtainRef,
    ]);

    return { isOnMenuPage, refetchWithConsent };
};
